import Hotkeys from 'react-hot-keys';
import { PureComponent, ReactNode } from 'react';
import { ObjectType, SlideFromRightWrapper } from '../../../../common/SlideFromRightWrapper';
import { TREATMENT_PLAN_TYPE, TreatmentPlan } from '../../../../../api/getTreatmentPlans';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { Collapse, Space, notification } from 'antd';
import { EKA_EVENT, dispatch as ekaEventDispatch } from '../../../../../bridge/events/dispatch';
import { TreatmentPlansFetchState } from './PPTreatmentPlan';
import TreatmentServicesTable from './TreatmentServicesTable';
import TreatmentPricing from './TreatmentPricing';
import TreatmentPaymentsTable from './TreatmentPaymentsTable';
import getTreatmentPlan, { Receipt } from '../../../../../api/getTreatmentPlan';
import moment from 'moment';
import { AntTooltip } from '../../../../common/AntTooltip';
import { formattedNumber, getPxPrintUrl } from '../../../../../pages/utils';
import { BillingPopupData } from './utils';
import WorkDoneTable from './WorkDoneTable';
import { DentalProcedure } from '../../../../../store/types/prescription';
import { getDoc } from '../../../../../store/idbHandler';
import { ON_CLOSE_BILLING_POPUP, Events } from '../../../../../bridge/events/types';
const { Panel } = Collapse;

interface Props {
    closeMe: () => void;
    treatmentBilling: TreatmentPlan;
    ptDetails: string;
    billingPopupData?: BillingPopupData;
    style?: ObjectType;
    isForWorkDone?: boolean;
    aId?: string;
    dentalProcedures?: DentalProcedure[];
    outsidePad?: boolean;
}

interface State {
    treatmentPlanFetchState?: TreatmentPlansFetchState;
    treatmentPlan?: TreatmentPlan;
    receipts?: Receipt[];
    treatmentProcedures?: DentalProcedure[];
    latestAId?: string;
    latestAIdWithWorkdone?: string;
    latestPxIdWithWorkdone?: string;
    activeTab: 'work-done' | 'bills' | 'payments';
    lastUpdated: string;
}

class TreatmentBilling extends PureComponent<Props, State> {
    state: State = {
        activeTab: this.props.isForWorkDone ? 'work-done' : 'bills',
        lastUpdated: new Date().toISOString(),
    };

    componentDidMount = async () => {
        const { fetchTreatmentPlan, onEkaEvents } = this;
        fetchTreatmentPlan();
        window.addEventListener(EKA_EVENT, onEkaEvents);
    };

    componentWillUnmount = (): void => {
        const { onEkaEvents } = this;
        window.removeEventListener(EKA_EVENT, onEkaEvents);
    };

    onEkaEvents: EventListener = (e: Event): void => {
        const { fetchTreatmentPlan } = this;
        const { detail } = e as CustomEvent<Events>;
        switch (detail.type) {
            case ON_CLOSE_BILLING_POPUP:
                fetchTreatmentPlan();
                break;
        }
    };

    fetchTreatmentPlan = async () => {
        const { treatmentBilling, aId } = this.props;
        const { plan_id } = treatmentBilling;
        this.setState({
            treatmentPlanFetchState: 'loading',
            treatmentPlan: undefined,
            receipts: undefined,
            treatmentProcedures: undefined,
            latestAId: undefined,
            latestPxIdWithWorkdone: undefined,
            latestAIdWithWorkdone: undefined,
        });
        try {
            const {
                success,
                treatmentPlan,
                error = '',
                receipts,
                treatmentProcedures,
                latestAId,
                latestPxIdWithWorkdone,
                latestAIdWithWorkdone,
            } = await getTreatmentPlan(plan_id, aId || '');
            if (success) {
                this.setState({
                    treatmentPlanFetchState: 'success',
                    treatmentPlan: treatmentPlan,
                    receipts: receipts,
                    treatmentProcedures: treatmentProcedures,
                    latestAId: latestAId,
                    latestPxIdWithWorkdone: latestPxIdWithWorkdone,
                    latestAIdWithWorkdone: latestAIdWithWorkdone,
                    lastUpdated: new Date().toISOString(),
                });
            } else {
                this.setState({
                    treatmentPlanFetchState: 'error',
                });
                captureErrorString(error);
                track('ERROR', {
                    on: 'getTreatmentPlan',
                    error: error,
                });
                notification.error({
                    message: 'Error in fetching treatment plan',
                    duration: 5,
                });
            }
        } catch (error) {
            this.setState({
                treatmentPlanFetchState: 'error',
            });
            captureError(error as Error);
            track('ERROR', {
                on: 'getTreatmentPlan',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in fetching treatment plan',
                duration: 5,
            });
        }
    };

    onAddNew = () => {
        const { billingPopupData } = this.props;
        if (!billingPopupData) {
            return;
        }
        const {
            visitId,
            uhid,
            mobile,
            uuid,
            fln,
            oid,
            clinicId,
            source,
            aptDate,
        } = billingPopupData;
        ekaEventDispatch({
            type: 'BILLING_POPUP',
            data: {
                // TODO: for whom doctor this appt belongs tos
                docid: getDoc()?._id,
                aid: visitId,
                uhid: uhid,
                patient: {
                    mobile: mobile,
                    uuid: uuid,
                    fln: fln,
                    oid: oid,
                },
                clinicId: clinicId,
                source: source,
                aptDate: aptDate,
            },
        });
        track('ON_ADD_NEW_BILL');
    };

    onPrint = () => {
        const { latestPxIdWithWorkdone } = this.state;
        const url = getPxPrintUrl(latestPxIdWithWorkdone || '');
        window.open(url);
        track('ON_PRINT_WORK_DONE');
    };

    render(): ReactNode {
        const {
            closeMe,
            ptDetails,
            style,
            billingPopupData,
            isForWorkDone,
            aId,
            dentalProcedures,
            outsidePad,
        } = this.props;
        const {
            receipts,
            activeTab,
            treatmentPlanFetchState,
            treatmentPlan,
            lastUpdated,
            treatmentProcedures,
            latestAId,
            latestAIdWithWorkdone,
        } = this.state;
        const { onAddNew, fetchTreatmentPlan, onPrint } = this;
        let totalBills = 0;
        if (treatmentPlan) {
            const totalAmount = treatmentPlan.services.reduce((acc, cur) => acc + cur.amount, 0);
            const additionalDiscount = (treatmentPlan.discount / 100) * totalAmount;
            totalBills = totalAmount - additionalDiscount;
        }
        const totalPayments =
            receipts?.reduce((acc, cur) => {
                return acc + cur.net_amount;
            }, 0) || 0;
        const treatmentProceduresFromRx = dentalProcedures?.filter((i) => Boolean(i.service_id));
        const isCompletedTreatmentPlan = treatmentPlan?.type === TREATMENT_PLAN_TYPE.COMPLETED_PLAN;

        return (
            <Hotkeys keyName="esc">
                <SlideFromRightWrapper
                    closeMe={() => closeMe()}
                    style={style || { width: '1000px' }}
                >
                    <div className="h-full flex flex-col overflow-hidden">
                        <div className="bg-white px-16 py-12 flex items-center justify-between border-b border-darwin-neutral_new-200">
                            <div className="flex items-center space-x-8">
                                <p className="text-20 text-darwin-neutrals-1000">
                                    {isForWorkDone ? 'Work Done Sheet' : 'Treatment Billing'}
                                </p>
                                {ptDetails ? (
                                    <div className="flex items-center space-x-4 text-darwin-neutral_new-800 relative top-1">
                                        <p>for</p>
                                        <p className="font-700">{ptDetails}</p>
                                    </div>
                                ) : (
                                    ''
                                )}
                                <div className="flex relative top-2 items-center gap-4">
                                    <p className="text-neutral-5 text-13">
                                        Last updated at {moment(lastUpdated).format('hh:mm A')}
                                    </p>
                                    <AntTooltip content="Refresh">
                                        <div className="flex justify-between p-4 select-none items-center space-x-16">
                                            <div className="flex space-x-8 items-center">
                                                <button
                                                    onClick={fetchTreatmentPlan}
                                                    className="flex space-x-8 items-center border-b border-transparent cursor-pointer text-actionPrimary hover:text-actionPrimary-hover hover:border-actionPrimary-hover font-700 focus:outline-none text-14"
                                                >
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        className="fill-darwin-accent-bright-blue-800"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88C178.39 88.068 111.52 141.178 93.057 214.85C91.713 220.213 86.935 224 81.406 224H24.103C16.605 224 10.909 217.193 12.296 209.824C33.933 94.924 134.813 8 256 8C322.448 8 382.791 34.136 427.315 76.685L463.03 40.97C478.149 25.851 504 36.559 504 57.941V192C504 205.255 493.255 216 480 216H345.941C324.559 216 313.851 190.149 328.97 175.029L370.72 133.28V133.28ZM32 296H166.059C187.441 296 198.149 321.851 183.03 336.971L141.28 378.721C172.542 407.994 213.115 424.04 256.156 424.001C333.574 423.931 400.471 370.857 418.943 297.152C420.287 291.789 425.065 288.002 430.594 288.002H487.898C495.396 288.002 501.092 294.809 499.705 302.178C478.067 417.076 377.187 504 256 504C189.552 504 129.209 477.864 84.685 435.315L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320C8 306.745 18.745 296 32 296Z"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </AntTooltip>
                                </div>
                            </div>
                            <div className="flex items-center space-x-16">
                                {latestAId !== latestAIdWithWorkdone && (
                                    <svg
                                        className="w-14 h-14 fill-darwin-accent-bright-blue-800 cursor-pointer"
                                        viewBox="0 0 14 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                        onClick={onPrint}
                                    >
                                        <path d="M3.34375 4.1875H2.125V1.75C2.125 0.861328 2.83594 0.125 3.75 0.125H9.56445C9.99609 0.125 10.4023 0.302734 10.707 0.607422L11.3926 1.29297C11.6973 1.59766 11.875 2.00391 11.875 2.43555V4.1875H10.6562V2.43555C10.6562 2.33398 10.6055 2.23242 10.5293 2.15625L9.84375 1.4707C9.76758 1.39453 9.66602 1.34375 9.56445 1.34375H3.75C3.52148 1.34375 3.34375 1.54688 3.34375 1.75V4.1875ZM3.75 9.46875V11.9062H10.25V9.46875H3.75ZM3.34375 8.25H10.6562C11.0879 8.25 11.4688 8.63086 11.4688 9.0625H12.2812V6.625C12.2812 6.42188 12.0781 6.21875 11.875 6.21875H2.125C1.89648 6.21875 1.71875 6.42188 1.71875 6.625V9.0625H2.53125C2.53125 8.63086 2.88672 8.25 3.34375 8.25ZM11.4688 10.2812V12.3125C11.4688 12.7695 11.0879 13.125 10.6562 13.125H3.34375C2.88672 13.125 2.53125 12.7695 2.53125 12.3125V10.2812H1.3125C0.855469 10.2812 0.5 9.92578 0.5 9.46875V6.625C0.5 5.73633 1.21094 5 2.125 5H11.875C12.7637 5 13.5 5.73633 13.5 6.625V9.46875C13.5 9.92578 13.1191 10.2812 12.6875 10.2812H11.4688Z" />
                                    </svg>
                                )}
                                <svg
                                    className="w-14 h-14 fill-darwin-neutral_new-800 cursor-pointer"
                                    viewBox="0 0 12 12"
                                    xmlns="http://www.w3.org/2000/svg"
                                    onClick={closeMe}
                                >
                                    <path d="M10.7812 2.28125L7.03125 6.03125L10.75 9.75C11.0625 10.0312 11.0625 10.5 10.75 10.7812C10.4688 11.0938 10 11.0938 9.71875 10.7812L5.96875 7.0625L2.25 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 2 0.90625 1.53125 1.21875 1.21875C1.5 0.9375 1.96875 0.9375 2.28125 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.7812 1.25C11.0625 1.53125 11.0625 2 10.7812 2.28125Z" />
                                </svg>
                            </div>
                        </div>
                        {!isForWorkDone && (
                            <div className="bg-white px-16 flex items-center justify-between border-b border-darwin-neutral_new-200">
                                <div>
                                    <button
                                        className={`px-16 py-8 space-y-4 text-left border-b-2 ${
                                            activeTab === 'bills'
                                                ? 'text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                                : 'text-darwin-neutral_new-800 border-white'
                                        }`}
                                        onClick={() => {
                                            this.setState({
                                                activeTab: 'bills',
                                            });
                                        }}
                                    >
                                        <p>Bills</p>
                                        {treatmentPlanFetchState === 'loading' ? (
                                            <p className="shimmer h-24 w-64"></p>
                                        ) : (
                                            <p className="min-w-64 text-16 font-700">
                                                ₹{formattedNumber(totalBills)}
                                            </p>
                                        )}
                                    </button>
                                    <button
                                        className={`px-16 py-8 space-y-4 text-left border-b-2 ${
                                            activeTab === 'payments'
                                                ? 'text-darwin-accent-bright-blue-800 border-darwin-accent-bright-blue-800'
                                                : 'text-darwin-neutral_new-800 border-white'
                                        }`}
                                        onClick={() => {
                                            this.setState({
                                                activeTab: 'payments',
                                            });
                                        }}
                                    >
                                        <p>Payments</p>
                                        {treatmentPlanFetchState === 'loading' ? (
                                            <p className="shimmer h-24 w-36"></p>
                                        ) : (
                                            <p className="min-w-36 text-16 font-700 text-darwin-green-primary relative -top-1">
                                                ₹{formattedNumber(totalPayments)}
                                            </p>
                                        )}
                                    </button>
                                </div>
                                <div
                                    className="px-16 py-12 flex items-center space-x-8 rounded-8"
                                    style={{
                                        background: '#FBEDEC',
                                    }}
                                >
                                    <svg
                                        className="w-14 h-14 fill-red-primary"
                                        viewBox="0 0 14 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM7 3.875C7.33008 3.875 7.60938 4.1543 7.60938 4.48438V7.32812C7.60938 7.68359 7.33008 7.9375 7 7.9375C6.64453 7.9375 6.39062 7.68359 6.39062 7.32812V4.48438C6.39062 4.1543 6.64453 3.875 7 3.875ZM7.8125 9.5625C7.8125 10.0195 7.43164 10.375 7 10.375C6.54297 10.375 6.1875 10.0195 6.1875 9.5625C6.1875 9.13086 6.54297 8.75 7 8.75C7.43164 8.75 7.8125 9.13086 7.8125 9.5625Z" />
                                    </svg>
                                    <p className="text-13 text-red-primary">
                                        Total due:{' '}
                                        <span className="font-700">
                                            ₹{formattedNumber(totalBills - totalPayments)}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        )}
                        {treatmentPlanFetchState === 'loading' ? (
                            <div className="p-16 flex-1 flex items-center justify-center bg-darwin-neutrals-50">
                                <svg
                                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </div>
                        ) : activeTab === 'work-done' && treatmentPlan ? (
                            <div className="p-16 flex-1 flex flex-col overflow-auto bg-darwin-neutrals-50 space-y-10">
                                <p className="text-red-primary">
                                    *{' '}
                                    {outsidePad
                                        ? 'You can add workdone only from Rx, please start visit'
                                        : 'Please finish the prescription to print all work done'}
                                </p>
                                {treatmentPlan.services.map((i) => {
                                    const proceduresArray =
                                        aId === latestAId &&
                                        Array.isArray(treatmentProceduresFromRx) &&
                                        treatmentProceduresFromRx.length > 0
                                            ? treatmentProceduresFromRx
                                            : Array.isArray(treatmentProcedures)
                                            ? treatmentProcedures
                                            : [];
                                    const filteredTreatmentProcedures = proceduresArray.filter(
                                        (j) => j.service_id === i.id,
                                    );
                                    return (
                                        <div
                                            className="rounded-8 space-y-12 px-16 pt-12 pb-16 bg-white"
                                            style={{
                                                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-8">
                                                    <div
                                                        className="w-28 h-28 flex items-center justify-center rounded-4"
                                                        style={{ background: '#E8E8E8' }}
                                                    >
                                                        <svg
                                                            className="w-16 h-13 fill-darwin-neutral_new-800"
                                                            viewBox="0 0 16 13"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path d="M0.6875 3.375C0.6875 2.0293 1.7793 0.9375 3.125 0.9375H12.875C14.2207 0.9375 15.3125 2.0293 15.3125 3.375V9.875C15.3125 11.2207 14.2207 12.3125 12.875 12.3125H3.125C1.7793 12.3125 0.6875 11.2207 0.6875 9.875V3.375ZM5.15625 4.59375V6.01562C5.15625 6.37109 5.41016 6.625 5.76562 6.625H6.98438C7.31445 6.625 7.59375 6.37109 7.59375 6.01562V4.59375C7.59375 3.93359 7.03516 3.375 6.375 3.375C5.68945 3.375 5.15625 3.93359 5.15625 4.59375ZM9.625 3.375C8.93945 3.375 8.40625 3.93359 8.40625 4.59375V6.01562C8.40625 6.37109 8.66016 6.625 9.01562 6.625H10.2344C10.5645 6.625 10.8438 6.37109 10.8438 6.01562V4.59375C10.8438 3.93359 10.2852 3.375 9.625 3.375ZM1.90625 5.40625V6.01562C1.90625 6.37109 2.16016 6.625 2.51562 6.625H3.73438C4.06445 6.625 4.34375 6.37109 4.34375 6.01562V5.40625C4.34375 4.74609 3.78516 4.1875 3.125 4.1875C2.43945 4.1875 1.90625 4.74609 1.90625 5.40625ZM3.125 9.875C3.78516 9.875 4.34375 9.3418 4.34375 8.65625V8.04688C4.34375 7.7168 4.06445 7.4375 3.73438 7.4375H2.51562C2.16016 7.4375 1.90625 7.7168 1.90625 8.04688V8.65625C1.90625 9.3418 2.43945 9.875 3.125 9.875ZM5.15625 8.65625C5.15625 9.3418 5.68945 9.875 6.375 9.875C7.03516 9.875 7.59375 9.3418 7.59375 8.65625V8.04688C7.59375 7.7168 7.31445 7.4375 6.98438 7.4375H5.76562C5.41016 7.4375 5.15625 7.7168 5.15625 8.04688V8.65625ZM9.625 9.875C10.2852 9.875 10.8438 9.3418 10.8438 8.65625V8.04688C10.8438 7.7168 10.5645 7.4375 10.2344 7.4375H9.01562C8.66016 7.4375 8.40625 7.7168 8.40625 8.04688V8.65625C8.40625 9.3418 8.93945 9.875 9.625 9.875ZM11.6562 5.40625V6.01562C11.6562 6.37109 11.9102 6.625 12.2656 6.625H13.4844C13.8145 6.625 14.0938 6.37109 14.0938 6.01562V5.40625C14.0938 4.74609 13.5352 4.1875 12.875 4.1875C12.1895 4.1875 11.6562 4.74609 11.6562 5.40625ZM12.875 9.875C13.5352 9.875 14.0938 9.3418 14.0938 8.65625V8.04688C14.0938 7.7168 13.8145 7.4375 13.4844 7.4375H12.2656C11.9102 7.4375 11.6562 7.7168 11.6562 8.04688V8.65625C11.6562 9.3418 12.1895 9.875 12.875 9.875Z" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-16 font-700 text-darwin-neutral-1000">
                                                        {i.name}
                                                    </p>
                                                </div>
                                                <div>
                                                    <svg
                                                        className="w-4 h-11 fill-darwin-accent-bright-blue-800"
                                                        viewBox="0 0 4 11"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M2 8.46875C2.43164 8.46875 2.8125 8.72266 3.04102 9.07812C3.26953 9.45898 3.26953 9.94141 3.04102 10.2969C2.8125 10.6777 2.43164 10.9062 2 10.9062C1.54297 10.9062 1.16211 10.6777 0.933594 10.2969C0.705078 9.94141 0.705078 9.45898 0.933594 9.07812C1.16211 8.72266 1.54297 8.46875 2 8.46875ZM2 4.40625C2.43164 4.40625 2.8125 4.66016 3.04102 5.01562C3.26953 5.39648 3.26953 5.87891 3.04102 6.23438C2.8125 6.61523 2.43164 6.84375 2 6.84375C1.54297 6.84375 1.16211 6.61523 0.933594 6.23438C0.705078 5.87891 0.705078 5.39648 0.933594 5.01562C1.16211 4.66016 1.54297 4.40625 2 4.40625ZM3.21875 1.5625C3.21875 2.01953 2.96484 2.40039 2.60938 2.62891C2.22852 2.85742 1.74609 2.85742 1.39062 2.62891C1.00977 2.40039 0.78125 2.01953 0.78125 1.5625C0.78125 1.13086 1.00977 0.75 1.39062 0.521484C1.74609 0.292969 2.22852 0.292969 2.60938 0.521484C2.96484 0.75 3.21875 1.13086 3.21875 1.5625Z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <WorkDoneTable
                                                treatmentProcedures={filteredTreatmentProcedures}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : activeTab === 'bills' && treatmentPlan ? (
                            <div className="p-16 flex-1 flex flex-col overflow-auto bg-darwin-neutrals-50 space-y-8">
                                <Space
                                    direction="vertical"
                                    className="w-full rounded-8"
                                    style={{
                                        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.15)',
                                    }}
                                >
                                    <Collapse
                                        expandIcon={() => (
                                            <svg
                                                className={`relative -top-2 w-12 h-7 fill-darwin-neutral_new-800`}
                                                viewBox="0 0 12 7"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M5.56836 6.30664L0.693359 1.43164C0.439453 1.20312 0.439453 0.822266 0.693359 0.568359C0.921875 0.339844 1.30273 0.339844 1.55664 0.568359L6 5.03711L10.4434 0.59375C10.6719 0.339844 11.0527 0.339844 11.3066 0.59375C11.5352 0.822266 11.5352 1.20312 11.3066 1.43164L6.40625 6.30664C6.17773 6.56055 5.79688 6.56055 5.56836 6.30664Z" />
                                            </svg>
                                        )}
                                        activeKey={treatmentPlan.plan_id}
                                    >
                                        <Panel
                                            header={
                                                <div className="font-700">{treatmentPlan.name}</div>
                                            }
                                            key={treatmentPlan.plan_id}
                                        >
                                            <div className="space-y-16">
                                                <TreatmentServicesTable
                                                    services={treatmentPlan.services}
                                                />
                                                <TreatmentPricing treatmentPlan={treatmentPlan} />
                                            </div>
                                        </Panel>
                                    </Collapse>
                                </Space>
                            </div>
                        ) : (
                            <div className="p-16 flex-1 flex flex-col overflow-auto bg-darwin-neutrals-50 space-y-8">
                                {Array.isArray(receipts) && (
                                    <TreatmentPaymentsTable receipts={receipts} />
                                )}
                                {billingPopupData && !isCompletedTreatmentPlan && (
                                    <div>
                                        <button
                                            className="px-12 py-10 text-13 font-700 text-darwin-accent-bright-blue-800"
                                            onClick={onAddNew}
                                        >
                                            + Add New
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </SlideFromRightWrapper>
            </Hotkeys>
        );
    }
}

export default TreatmentBilling;
